import React from 'react';
import { motion } from 'framer-motion';
import { CodeBracketIcon, DevicePhoneMobileIcon, PaintBrushIcon, CogIcon, RocketLaunchIcon, CheckIcon } from '@heroicons/react/24/outline';

const Services = () => {
  const services = [
    {
      id: 1,
      title: "Frontend Development",
      description: "Modern, responsive web applications using React, JavaScript, and cutting-edge technologies.",
      icon: <CodeBracketIcon className="w-8 h-8" />,
      features: ["React & Next.js", "Responsive Design", "Performance Optimization", "Modern UI/UX"],
      price: "Starting at ₹40,000",
      popular: false
    },
    {
      id: 2,
      title: "Full-Stack Development",
      description: "Complete web solutions from frontend to backend, database design, and deployment.",
      icon: <CogIcon className="w-8 h-8" />,
      features: ["Frontend + Backend", "Database Design", "API Development", "Cloud Deployment"],
      price: "Starting at ₹95,000",
      popular: true
    },
    {
      id: 3,
      title: "Web App Development",
      description: "Custom web applications tailored to your business needs with modern technologies.",
      icon: <RocketLaunchIcon className="w-8 h-8" />,
      features: ["Custom Solutions", "Scalable Architecture", "Third-party Integrations", "Maintenance & Support"],
      price: "Starting at ₹65,000",
      popular: false
    },
    {
      id: 4,
      title: "UI/UX Design",
      description: "Beautiful, user-friendly interfaces that provide excellent user experience.",
      icon: <PaintBrushIcon className="w-8 h-8" />,
      features: ["User Research", "Wireframing", "Prototyping", "Design Systems"],
      price: "Starting at ₹32,000",
      popular: false
    }
  ];

  return (
    <section id="services" className="py-20 bg-white dark:bg-gray-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Services & Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Professional web development services to bring your ideas to life. From simple websites to complex applications.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className={`relative bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
                service.popular ? 'ring-2 ring-gray-900 dark:ring-white' : ''
              }`}
            >
              {/* Popular Badge */}
              {service.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-full text-sm font-bold">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className="w-16 h-16 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl flex items-center justify-center mb-6">
                {service.icon}
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {service.title}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Price */}
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {service.price}
                </div>
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                    service.popular
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16 bg-gray-50 dark:bg-gray-800 rounded-2xl p-12"
        >
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Need Something Custom?
          </h3>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Every project is unique. Let's discuss your specific requirements and create a tailored solution for your needs.
          </p>
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Let's Talk About Your Project
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;